import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, ArrowRight, Linkedin, Facebook, Instagram } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

/**
 * Compact homepage strip for Board of Directors.  Shows headshots in a
 * single row with name + role under each.  Links to the dedicated /board
 * page for full bios.  Quiet on empty (returns null).
 */
export default function BoardStrip() {
  const { lang } = useI18n();
  const [directors, setDirectors] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/board-of-directors")
      .then((r) => { if (alive) setDirectors(r.data?.directors || []); })
      .catch(() => { if (alive) setDirectors([]); });
    return () => { alive = false; };
  }, []);

  if (directors.length === 0) return null;

  return (
    <section className="bg-white border-t border-warmgray-200" data-testid="board-strip">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald2/10 text-emerald2">
              <Crown className="w-3 h-3" />
              <span className="text-[10px] uppercase tracking-[0.24em] font-semibold">
                {lang === "en" ? "Governance" : "Hội đồng Quản trị"}
              </span>
            </div>
            <h2 className="mt-3 font-serif text-2xl md:text-3xl text-charcoal">
              {lang === "en" ? "Board of Directors" : "Hội đồng Quản trị"}
            </h2>
          </div>
          <Link
            to="/board"
            data-testid="board-strip-link"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-emerald2 hover:underline"
          >
            {lang === "en" ? "Meet the board" : "Xem chi tiết"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          {directors.map((d) => (
            <Link
              key={d.id}
              to="/board"
              data-testid={`board-strip-${d.slug}`}
              className="group block"
            >
              <div className="aspect-square rounded-2xl overflow-hidden bg-warmgray-100">
                {d.photo_url ? (
                  <img
                    src={d.photo_url}
                    alt={d.full_name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-warmgray-400">
                    <Crown className="w-8 h-8" />
                  </div>
                )}
              </div>
              <div className="mt-3">
                <div className="font-serif text-base md:text-lg text-charcoal leading-tight">{d.full_name}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-emerald2 font-semibold leading-snug">
                  {d.board_title || (lang === "en" ? "Director" : "Thành viên HĐQT")}
                </div>
                {(d.linkedin_url || d.facebook_url || d.instagram_url) && (
                  <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {d.linkedin_url && (
                      <a
                        href={d.linkedin_url} target="_blank" rel="noreferrer"
                        aria-label={`${d.full_name} on LinkedIn`}
                        data-testid={`board-strip-linkedin-${d.slug}`}
                        className="text-warmgray-500 hover:text-emerald2 transition-colors"
                      >
                        <Linkedin className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {d.facebook_url && (
                      <a
                        href={d.facebook_url} target="_blank" rel="noreferrer"
                        aria-label={`${d.full_name} on Facebook`}
                        data-testid={`board-strip-facebook-${d.slug}`}
                        className="text-warmgray-500 hover:text-emerald2 transition-colors"
                      >
                        <Facebook className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {d.instagram_url && (
                      <a
                        href={d.instagram_url} target="_blank" rel="noreferrer"
                        aria-label={`${d.full_name} on Instagram`}
                        data-testid={`board-strip-instagram-${d.slug}`}
                        className="text-warmgray-500 hover:text-emerald2 transition-colors"
                      >
                        <Instagram className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 sm:hidden">
          <Link to="/board" className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald2">
            {lang === "en" ? "Meet the board" : "Xem chi tiết"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
